import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { logConnectorFailure, logConnectorSuccess } from "../../../telemetry";
import { fetchCalendarView } from "../services/calendarView";
import { addDays, dayStart } from "../domain/time";
import type { CalEvent } from "../types";

interface WeekEventsState {
  readonly events: CalEvent[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly truncated: boolean;
}

const EMPTY: WeekEventsState = { events: [], loading: false, error: null, truncated: false };

/** The signed-in user's events for the week starting Monday `mon` (Mon 00:00 to the next Mon 00:00, local). */
export function useWeekEvents(
  token: string | null,
  mon: string,
): WeekEventsState & { reload: () => void; refresh: () => Promise<void> } {
  const { refreshGraphToken } = useAuthState();
  const [state, setState] = useState<WeekEventsState>(EMPTY);
  const [reloadCount, setReloadCount] = useState(0);

  // The Graph token rotates on every silent refresh; reading it through a ref
  // keeps a rotation from refetching (and flashing "Loading…") mid-view.
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const hasToken = token !== null;

  useEffect(() => {
    const current = tokenRef.current;
    if (!current) {
      setState(EMPTY);
      return;
    }

    let cancelled = false;
    setState({ ...EMPTY, loading: true });

    fetchCalendarView(current, refreshGraphToken, dayStart(mon), dayStart(addDays(mon, 7)))
      .then(({ events, truncated }) => {
        if (cancelled) return;
        setState({ events, loading: false, error: null, truncated });
        logConnectorSuccess("calendar", "load-events");
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setState({ ...EMPTY, error: e instanceof Error ? e.message : "Failed to load calendar" });
        logConnectorFailure("calendar", e, "load-events");
      });

    return () => {
      cancelled = true;
    };
  }, [hasToken, mon, reloadCount, refreshGraphToken]);

  const reload = useCallback(() => setReloadCount((n) => n + 1), []);

  // Re-read the same week after a change is sent, keeping the current events on
  // screen meanwhile (no "Loading…"). A failed re-read leaves them as they are.
  const monRef = useRef(mon);
  monRef.current = mon;
  const refresh = useCallback(async () => {
    const current = tokenRef.current;
    const week = monRef.current;
    if (!current) return;
    try {
      const { events, truncated } = await fetchCalendarView(
        current,
        () => refreshGraphToken(false),
        dayStart(week),
        dayStart(addDays(week, 7)),
      );
      if (monRef.current === week) setState({ events, loading: false, error: null, truncated });
    } catch (e) {
      logConnectorFailure("calendar", e, "refresh-events");
    }
  }, [refreshGraphToken]);

  return { ...state, reload, refresh };
}
